import { Component, Fragment } from 'react'
import styles from './Viewer.module.css'
import { Button, Input, Label } from 'reactstrap'
import { FormGroup } from './FormGroup'
import { Dialog, DialogHeader, DialogContent, DialogFooter } from './Dialog'
import axios, { AxiosResponse, isAxiosError } from 'axios'
import { AppConfigurationTopBar } from './ConfigurationTopBar'
import {Header} from './Header'
import Footer from './Footer'
import Notification from './Notification'
import Area from './Area'
import Spinner from './Spinner'
import {defaultViewerConfig} from './default-config'
// @ts-expect-error
import { Helmet } from 'react-helmet'
import { Dialog as InfoDialog, MenuItem, Select } from '@mui/material'
import { inflateLayout, LAYOUT_NAME, type LayoutName } from '../helpers/layout'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { AutoHyperlink } from './AutoHyperlink'
import { FETCH_VIEWER_SUCCESS, SET_LAYOUT_NAME } from '../redux/project'
import { fixupLegacyRepoURL, getCgiUrl } from "../helpers/url"
import { getAndSetDefaultParameterValues } from './ViewerContextUtils'
import { FontListButton } from "./FontList"
import { SparqlTriggerExecutor } from './SparqlTriggerExecutor'
import { JsonEditor, JsonTextEditor } from "./JsonEditor"
import { format as formatDate } from "date-fns"
import "typeface-exo"
import "@fontsource-variable/exo-2"
import { AppVarsInUrlSyncer } from './AppVarsInUrlSyncer'
import { EventLogStoreProvider } from './EventLogs/event-log-store'
import { RuleExecutionLogsStoreProvider } from './RuleExecutionLogs/rule-execution-logs-store'
import { loadConfigLocal, saveConfigLocal } from '../model/local-mode'
import { LabelWithHelp } from './FormField'
import { getAuthHeaders, useWistorAuth, WistorAuth } from '../api/auth'
import { useDispatch, useSelector } from '../store'
import { type Dispatch } from 'redux'
import { RouteComponentProps } from 'react-router-dom'
import { Trigger } from './TriggerPopup'
import { WidgetConfig } from './WidgetConfiguration'
import { AppLoginStatus } from '../redux/app-login'
import { FeatureFlagValues, useFeatureFlags } from '../flags'
import { SingleLoginUnauthorizedView } from './SingleLogin'

declare global {
  interface Window {
    PROJECT?: string // lock&publish
    VIEWER?: string // lock&publish
    CONFIG_URL?: string // lock&publish
  }
  interface Document {
    userParameters?: {userName?: string, userUri?: string}
    st?: () => void
    preview?: () => void
    edit?: () => void
  }
}

type State = {
  singleLoginError?: "FORBIDDEN"
  mode: 'edit' | 'preview'
  popupInfoVisible: boolean
  dialogVisible: boolean
  notification: null | {
    type: 'success' | 'failure'
    text: string
  }
  loading: boolean
  showJSONEditor: boolean
  config: {} | null
  notShowTop?: boolean
  viewer: Viewer
  viewerOriginal: Viewer
  update60Running?: boolean
}

type Props
  = RouteComponentProps
  & {
    update60Running?: boolean // DEBT: this is not passed anywhere
    auth: WistorAuth
    dispatch: Dispatch
    project: any
    pubsub: any
    flags: FeatureFlagValues
  }

type Viewer // DEBT: not sure yet how proper these types are
  = {
      id: string
      public?: boolean
      createdAt: string
      project: Project
      user: {}
      path: string
      new?: boolean
      deleted?: boolean
    }
  & {
      title: string
      subtitle: string
      infoMessage?: string
      bbsparqlendpoint: string
      repoURL: string
      localRepoURL?: string
      mainArea: WidgetConfig
      triggers: Trigger[]
      sparqlErrorMessage?: string
      parameterValues?: string
      popupInfoAtStart?: boolean
      update60Enabled?: boolean
      parseUrlProperties?: boolean
      prejs?: string
    }
  & {
      logoURL?: string
      logoutbutton?: boolean
      layoutName: "CUSTOM" | "ORIGINAL"
      notShowTop?: boolean
      notShowBottom?: boolean
      pageWidth?: string
      css: string
    }
  & {
      updateURLVariables?: string[]
      pushURLVariables?: string[]
  }

type Project = {
  title: string,
  path: string,
  createdAt: string,
}

export class ViewerComponent extends Component<Props, State> {

  private static _instance: null | ViewerComponent = null

  timer?: ReturnType<typeof setTimeout> // DEBT: name too generic, maybe feature should be fixed
  keepAliveInterval: ReturnType<typeof setTimeout> | null = null
  autoLogin: Promise<AxiosResponse> | null = null
  update60?: boolean // DEBT: something that was used only for icdd-viewer, implementation should at least be cleaned up
  loop60?: boolean // DEBT: see above
  loadViewerIntoStateOnlyOnce?: boolean
  savingPromise: Promise<void> | null = null

  state: State = { // DEBT: not sure yet how to deal with this initial state
    singleLoginError: undefined,
    mode: 'preview',
    popupInfoVisible: false,
    dialogVisible: false,
    notification: null,
    loading: true,
    showJSONEditor: false,
    config: null,
    viewer: {} as Viewer, // DEBT: whilst converting to typescript, not clear what state should be like to make everything coherent
    viewerOriginal: {} as Viewer, // DEBT: whilst converting to typescript, not clear what state should be like to make everything coherent
  }

  static getInstance() {
    return ViewerComponent._instance
  }

  onJsonEditClick = () => {
    this.setState({ showJSONEditor: true })
  }

  constructRepoLoginPath(bbsparqlendpoint: string, repoUrl: string): string {
    const { viewerPath } = getViewProps(this.props)
    const repoLoginParams = new URLSearchParams(bbsparqlendpoint)
    repoLoginParams.set('repoUrl', repoUrl)
    repoLoginParams.set('viewerPath', viewerPath)

    return '/loginbb?id=' + encodeURIComponent(btoa('?' + repoLoginParams.toString()))
  }

  async componentDidMount() {
    const { projectPath, viewerPath } = getViewProps(this.props)

    ViewerComponent._instance = this

    let viewer = this.props.project.viewers.find((v: any) => v.path === viewerPath)
    if (viewer && viewer.loaded) {
        console.log("component did mount load into state");
      return this.loadViewerIntoState(viewer)
    }

    // no idea where CONFIG_URL comes from....
      if ((window.CONNECTED_APPS_STATIC) && (window.CONFIG_URL==null))
      {
        window.CONFIG_URL="./config.json";
      }

    const request = window.CONNECTED_APPS_STATIC
      ? {
          method: 'get',
          url: window.CONFIG_URL,
          headers: { 'Content-Type': 'application/json' },
        }
      : {
          method: 'get',
          url: `/api/projects/${projectPath}/viewers/${viewerPath}`,
          headers: getAuthHeaders(),
        }

    let config
    try {
     // console.log(request);

    if (!this.props.flags.localMode) {
      const configResponse = await axios(request)
      config = configResponse.data
    } else {
      config = loadConfigLocal()
    }

    } catch(error) {
      if (!isAxiosError(error)) throw error
      if (error.response?.status === 401) return this.props.history.push('/login', `projects/${projectPath}/${viewerPath}`)
      if (error.response?.status === 404) return this.loadNewViewerConfig(viewerPath)
      throw error
    }
    if (!config) throw new Error('somehow no config was fetched')

    if (window.CONNECTED_APPS_STATIC == null && config.repoURL.startsWith('http') && !this.props.flags.localMode)
    {
      if (config.repoURL.includes("BloodBee"))
      {
            // (Hans: so annoying so skipping this when it contains BloodBee
      }
      else
      {
        alert(`Attention: This app interacts with external triplestore-endpoint "${config.repoURL}". Ignore this message if this is on purpose. This message will not show up when this app is locked-and-published.`)
      }

    }

    if (config.prejs != null) {
      try {
        eval(config.prejs) // eslint-disable-line no-eval
      } catch (error2) {
        console.log(error2, config.prejs)
      }
    }

    const loadConfig = async () => {
      //console.log("load config load into state");
      await this.loadViewerIntoState(config)
      this.props.dispatch({ type: FETCH_VIEWER_SUCCESS, data: config })
      this.keepAlive('init')
    }

    if (window.location.search.toLowerCase().includes("debug=skiplogin")) {
      console.log("debug skiplogin");
      loadConfig();return;
    }


    if (shouldConnectToWistorBackend(config)) {
      const auth = await this.reauth(config)
      if (auth.isAutoLogin && !auth.success) return auth.goToLoginPage()

      const isAuthorised = await this.isAuthorised(config)

      if ('error' in isAuthorised) {
        return this.setState({singleLoginError: isAuthorised.error, loading: false})
      }

      this.props.dispatch({ type: "SET_APP_LOGIN_STATUS", payload: isAuthorised.loginStatus })

      const user = isAuthorised.user
      if (!user) {
        throw new Error('somehow no user data was fetched')
      }

      if (!isAuthorised.success) return isAuthorised.goToLoginPage()

      this.publish('user.fullAccess', Boolean(user.fullAccess))

      if (user.parameters!=null) document.userParameters=user.parameters;
      if (document.userParameters==null) document.userParameters={};
      document.userParameters.userUri=user.userUri;
      document.userParameters.userName=user.userName;
    }

    loadConfig()
  }

  keepAlive = (mode: 'init' | 'destroy') => {
    if (mode === 'init') {
      if (!this.keepAliveInterval) this.keepAliveInterval = setInterval(() => this.isAuthorised(), 5*60*1000)
      return
    }

    if (mode === 'destroy') {
      if (!this.keepAliveInterval) return
      clearInterval(this.keepAliveInterval)
      return this.keepAliveInterval = null
    }

    throw new Error(`unknown mode: ${JSON.stringify(mode)}`)
  }

  /**
   * @param {boolean} [options.silent=false] - If true, suppresses logging output and alerts. Unexpected errors are still logged.
   */
  async isAuthorised(
    config?: Parameters<typeof this.viewerParams>[0],
    options = { silent: false },
  ): Promise<{
    user: any,
    success: boolean,
    loginStatus: AppLoginStatus,
    goToLoginPage: () => any
  } | {
    error: 'FORBIDDEN'
  }> {
    const {isLoggedInUrl, viewerPath, goToLoginPage, repo, repo2} = this.viewerParams(config)
    let user
    try {
      const userResponse = await axios.get(isLoggedInUrl, {
        withCredentials: true,
        params: { viewerPath },
      })
      user = userResponse.data

    } catch(e) {
      if (isAxiosError(e) && e.response?.status === 403) {
        return { error: 'FORBIDDEN' }
      }
      console.error(e)
    }

    const loginStatus = (() => {
      if (!user) {
        if (!options?.silent) {
          console.error('no user data was fetched')
        }
        return "FETCH_ERROR"
      }

      if (user.loggedIn == null) {
        console.error('no user data was fetched (22)')
        return "FETCH_ERROR"
      }

      if (user.oidcLoginStatus === 'USER_NOT_PERMITTED' || user.oidcLoginStatus === 'REPO_NOT_PERMITTED') {
        if (!options?.silent) {
          alert('Your account does not have sufficient permissions for this application.')
        }
        return user.oidcLoginStatus
      }

      if (user.oidcLoginStatus === "PENDING_AUTHORIZATION") {
        return "PENDING_AUTHORIZATION"
      }

      if (user.loggedIn.toLowerCase() !== 'true') return "NOT_LOGGED_IN"

      if (repo == null || user.repo == null) return "FETCH_ERROR"

      if (repo.toLowerCase() !== user.repo.toLowerCase() && repo2.toLowerCase() !== user.repo.toLowerCase()) {
        if (!options?.silent) {
          alert('wrong repo. This account is not valid for this application.')
        }
        return "WRONG_REPO"
      }

      return "AUTHORIZED"
    })()

    return {
      user,
      goToLoginPage,
      success: loginStatus === "AUTHORIZED",
      loginStatus
    }
  }

  async reauth (
    config = this.state.viewer
  ): Promise<{ isAutoLogin: boolean, success: boolean, goToLoginPage: () => any }> {
    if (config == null) throw new Error('Reauth requested without any config loaded')

    const { isAutoLogin, psw, login, autoLoginUrl, goToLoginPage } = this.viewerParams(config)
    if (!isAutoLogin) return { isAutoLogin, goToLoginPage, success: false }

    let success
    try {
      this.autoLogin = this.autoLogin ?? axios({
        method: 'post',
        url: autoLoginUrl,
        withCredentials: true,
        data: { login, psw },
      })
      const response = await this.autoLogin
      success = response.data?.loggedIn?.toLowerCase?.() === 'true'

    } catch(error) {
      if (!isAxiosError(error)) throw error
      success = !(error?.response?.status === 401 || error?.response?.status === 403)
      if (!success) alert('Default login credentials are incorrect. Login with valid credentials and change it to fix this.')
    }

    this.autoLogin = null
    return { isAutoLogin, goToLoginPage, success }
  }

  viewerParams(
    config: Pick<Viewer, 'bbsparqlendpoint' | 'repoURL' | 'localRepoURL'> = this.state.viewer
  ) {
    const { projectPath, viewerPath } = getViewProps(this.props)

    const params = new URLSearchParams(config.bbsparqlendpoint)
    const repoName = params.get('repo')
    const repo2 = params.get('repo2') ?? ''
    const psw = params.get('psw')
    const login = params.get('login')
    const isAutoLogin = psw != null && login != null

    const repoURL = fixupLegacyRepoURL(config.repoURL, repoName)
    const localRepoURL = config.localRepoURL
      ? fixupLegacyRepoURL(config.localRepoURL, repoName)
      : undefined

    const sparqlUrl = getCgiUrl('sparql', repoName, repoURL, localRepoURL)
    const autoLoginUrl = getCgiUrl('login', repoName, repoURL, localRepoURL)
    const isLoggedInUrl = getCgiUrl('isloggedin', repoName, repoURL, localRepoURL)
    const repoLoginUrl = this.constructRepoLoginPath(config.bbsparqlendpoint, sparqlUrl)

    const goToLoginPage = () => this.props.history.replace(repoLoginUrl, this.props.location.pathname)

    return {projectPath, viewerPath, repo: repoName, repo2, isAutoLogin, psw, login, autoLoginUrl, sparqlUrl, isLoggedInUrl,
      repoURL, localRepoURL, repoLoginUrl, goToLoginPage}
  }

  loadNewViewerConfig(viewerPath: string) {
    if (this.props.flags.localMode) return false
    const deflatedConfig = { ...defaultViewerConfig, path: viewerPath, new: true }
    this.setState({
      viewer: inflateLayout(deflatedConfig as any) as Viewer, // tiny DEBT: could not be bothered with typing it right anymore
      viewerOriginal: inflateLayout(deflatedConfig as any) as Viewer, // tiny DEBT: could not be bothered with typing it right anymore
      loading: false,
    }, () => {
      this.save({ showNotification: false }) // Do not show 'saved' notification on creation of a new app
    })
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const viewer = this?.state?.viewer
    if (viewer == null) return

    if (viewer !== prevState.viewer) {
      this.props.dispatch({
        type: 'SET_VIEWER_CONFIG',
        data: viewer
      })
    }

    if (this.state.viewer.update60Enabled) {
      if (this.props.update60Running) {
      } else {
        if (this.update60) return;
        this.update60=true;
        var me =this;
        setTimeout(function () { me.changingProps()},500 );
      }
    } else {
      if ( (true) && (this.state.update60Running) ) {
        console.log("disabling update 60")
        this.props.dispatch({type: 'PUBLISH', data: {update60Running: false}})
      } else {
      }
    }
  }

   publish = (topic: string, value: any) => {
    this.props.dispatch({ type: 'PUBLISH', data: { [topic]: value } })
  }

  loadViewerIntoState(viewer: Viewer) {
    if (this.loadViewerIntoStateOnlyOnce!=null) return;
    this.loadViewerIntoStateOnlyOnce=true;

    return new Promise<void>((resolve, reject) => {
      try {
        this.setState({
          viewer: inflateLayout(viewer),
          viewerOriginal: inflateLayout(viewer),
          loading: false,
          popupInfoVisible: viewer.popupInfoAtStart ?? false,
        }, resolve)
        this.props.dispatch({ type: SET_LAYOUT_NAME, data: viewer.layoutName })
        this.registerAppCss(viewer.css)

        var r = getAndSetDefaultParameterValues(viewer);
        for (var key in r) {
        //  console.log("setting default parameters ",key,r[key])
          this.publish(key, r[key as keyof typeof r])
        }
      } catch(e) {
        reject(e)
      }
    })
  }

  saveTriggers = async (triggers: Trigger[]) => {
    await this.updateViewer({triggers})
    await this.save()
  }

  async updateViewer(partialViewer: Partial<Viewer>) {
    return new Promise<void>(resolve => {
      this.setState({ viewer: { ...this.state.viewer, ...partialViewer } }, resolve)
    })
  }

  uuidv4() {
    // @ts-expect-error: DEBT: clean up when it matters more to use than implementing login.wistor.nl auth
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
      (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
    )
  }
  changingProps() {
    if (this.state.update60Running) return
    if (this.props.update60Running) return
    // console.log("setting first update60 value");

    this.props.dispatch({
      type: 'PUBLISH',
      data: {
        update60: 'start',
        update60Running: true,
      },
    })

    this.changingProps60(true)
  }

  changingProps60(b: boolean) {
    if (b || this.state.viewer.update60Enabled) {
      var me = this
      if (me.loop60) return
      me.loop60 = true
      setTimeout(function () {
         console.log("changing update60 value ");
        let id = me.uuidv4()
        me.props.dispatch({
          type: 'PUBLISH',
          data: {
            update60: id,
          },
        })
       // console.log('changing 60')
        me.loop60 = false
        me.changingProps60(false)
      }, 60_000)
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timer)
    this.keepAlive('destroy')
    ViewerComponent._instance = null
  }

  onChange = (config: Viewer['mainArea']) => {
    let viewer = { ...this.state.viewer, mainArea: config }
    this.setState({ viewer })
  }

  selectLayout = (layoutName: LayoutName) => {
    let viewer = inflateLayout({ ...this.state.viewer, layoutName })
    this.setState({ viewer })
    this.props.dispatch({ type: SET_LAYOUT_NAME, data: layoutName })
  }

  save = async (opts?: {showNotification: false}) => {
    if (this.savingPromise) {
      try {
        await this.savingPromise
      } catch (e) {
        // ignore
      }
    }

    this.registerAppCss(this.state?.viewer?.css)
    this.savingPromise = this.saveImpl(opts)
    return this.savingPromise
  }

  saveImpl = async ({showNotification = true} = {}) => {
    if (this.props.flags.localMode) return saveConfigLocal(this.state.viewer)

    let viewer: Partial<Viewer> = this.state.viewer
    delete viewer.project

    const { projectPath, viewerPath } = getViewProps(this.props)

    try {
      const response = await axios({
        method: viewer.new ? 'post' : 'put',
        url: `/api/projects/${projectPath}/viewers` + (viewer.new ? '' : `/${viewerPath}`),
        headers: getAuthHeaders(),
        data: viewer,
      })
      const { data } = response

      if (showNotification) {
        if (this.timer) clearTimeout(this.timer)
        this.setState({ notification: { text: 'saved', type: 'success' } })
        this.timer = setTimeout(() => this.setState({ notification: null }), 3000)
      }

      const inflatedViewer = inflateLayout({ ...data, new: false })
      await this.setStatePromise({
        viewer: inflatedViewer,
        viewerOriginal: inflatedViewer
      })
      const {project, ...strippedData} = data
      this.props.dispatch({ type: 'SAVE_VIEWER_SUCCESS', data: strippedData })

    } catch(error) {

      if (!isAxiosError(error)) return
      if (error.response?.status === 401) {
        this.props.history.push('/login', `projects/${projectPath}/${viewerPath}`)
      } else {
        this.setState({
          notification: {
            text: 'not saved: ' + error.response?.data,
            type: 'failure',
          },
        })
        this.timer = setTimeout(() => this.setState({ notification: null }), 3000)
      }

    } finally {
      this.savingPromise = null
    }
  }

  setStatePromise<K extends keyof State>(
    setter: Pick<State, K> | ((prevState: Readonly<State>, props: Readonly<Props>) => Pick<State, K>)
  ): Promise<void> {
    return new Promise(resolve => {
      this.setState(setter, () => resolve())
    })
  }

  /** @param [appCssJson] appCss JSON-string of something that should be Record<string, string | number> */
  registerAppCss (appCssJson: string) {
    if (!appCssJson) return this.props.dispatch({type: 'SET_APP_CSS'})
    try {
      const appCss = JSON.parse(appCssJson)
      this.props.dispatch({type: 'SET_APP_CSS', data: appCss})
    } catch(e) {
      console.error(e)
    }
  }

  onDialogCancelClick = () => {
    this.setState({viewer: this.state.viewerOriginal})
    this.setState({ dialogVisible: false })
  }

  onDialogOkClick = () => {
    void this.save()
    this.setState({ dialogVisible: false })
  }

  jsonOKClick = () => {
    this.setState({ showJSONEditor: false })
  }

  voteToDisableUserActions = (voterId: string, userActionsDisabled: boolean) => {
    this.props.dispatch({
      type: "VOTE_USER_ACTIONS_DISABLED",
      data: { voterId, value: userActionsDisabled }
    })
  }

  render() {
    if (this == null) return <Spinner /> // DEBT: this should not be possible lol
    if (this.state == null) return <Spinner /> // DEBT: this should not be possible lol
    if (this.state.loading) return <Spinner />

    const { mode, projectPath, viewerPath } = getViewProps(this.props)

    if (this.state.singleLoginError) return (
      <SingleLoginUnauthorizedView
        error={this.state.singleLoginError}
        projectPath={projectPath}
      />
    )

    const viewer = this.state.viewer
    if (!viewer || viewer.deleted) return (
      <p>This viewer does not exist or has been deleted. Please contact an admin if this is a problem.</p>
    )

    document.st = () => this.setState({ notShowTop: false })
    document.preview = () => this.setState({ mode: 'preview' })
    document.edit = () => this.setState({ mode: 'edit' })

    let notification = this.state.notification

    const showTop = !viewer.notShowTop

    let css = {}
    if (viewer.css) {
      try {
        css = JSON.parse(viewer.css)
      } catch (error) {
        console.error(error)
      }
    }

    const { repo: repoName } = this.viewerParams(viewer)

    return (
      <EventLogStoreProvider>
      <RuleExecutionLogsStoreProvider>
      <div className={styles.wrapper} style={css}>
        <AppVarsInUrlSyncer varsToReplace={viewer.updateURLVariables} varsToPush={viewer.pushURLVariables}/>
        {viewer.triggers?.map(trigger => (
          <SparqlTriggerExecutor
            signalIsFetching={(isFetching) => {
              if (trigger.voteForUserActionsDisabledWhenLoading)
                this.voteToDisableUserActions(trigger.label, isFetching)
            }}
            key={trigger.label}
            templateId={trigger.label}
            endpoint={getCgiUrl('sparql', repoName, viewer.repoURL, viewer.localRepoURL)}
            sparqlTemplate={trigger.sparqlQuery}
            pubVars={this.props.pubsub}
            publish={this.publish}
          />))}
        <div className={styles.mainContainer}>

          <Helmet>
            <title>{viewer.title}</title>
            {viewer.project && (
              <link
                rel="icon"
                type="image/png"
                href={`${window.CONNECTED_APPS_STATIC ? '.' : ''}/favicon-${viewer.project.path}.png`}
                sizes="16x16"
              />
            )}
          </Helmet>
          {notification && <Notification messageType={notification.type} text={notification.text} />}

          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {!window.CONNECTED_APPS_STATIC && (
              <AppConfigurationTopBar
                openAppSettings={() => {this.setState({ dialogVisible: true}) }}
                mode={mode}
                projectPath={projectPath}
                viewerPath={viewerPath}
                repoURL={viewer.repoURL}
                saveTriggers={this.saveTriggers}
                triggers={viewer.triggers ?? []}
              />
            )}
            <div style={{ position: 'relative', flexGrow: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
              {showTop && (
                <Fragment>
                  <Header
                    title={viewer.title}
                    subtitle={viewer.subtitle}
                    repoURL={viewer.repoURL}
                    localRepoURL={viewer.localRepoURL}
                    bbsparqlendpoint={viewer.bbsparqlendpoint}
                    pageWidth={viewer.pageWidth}
                    parseUrlProperties={viewer.parseUrlProperties}
                    logoutbutton={viewer.logoutbutton}
                    noLoginButton
                    logoURL={viewer.logoURL}
                    onInfoClick={viewer.infoMessage ? () => this.setState({ popupInfoVisible: true }) : undefined}
                  />
                </Fragment>
              )}

              <div className={styles.main} style={{ maxWidth: viewer.pageWidth }}>
                <div className={styles.areaContainer}>
                  <Area
                    reauth={this.reauth.bind(this, viewer)}
                    config={viewer.mainArea}
                    onChange={this.onChange}
                    mode={mode}
                    repoName={repoName}
                    repoURL={viewer.repoURL}
                    localRepoURL={viewer.localRepoURL}
                    parseUrlProperties={viewer.parseUrlProperties}
                    sparqlErrorMessage={viewer.sparqlErrorMessage}
                    parameterValues={viewer.parameterValues}
                    save={this.save}
                  />
                </div>
              </div>

              {(this.state.viewer as any)?.layout?.showFooter && ( // DEBT: see layout.ts
                <Footer />
              )}

            </div>
          </div>

          <InfoDialog
            style={{ zIndex: 1337 }}
            maxWidth="xl"
            open={Boolean(this.state.popupInfoVisible && viewer.infoMessage)}
            onClose={() => this.setState({ popupInfoVisible: false })}
          >
            <div className={styles.infoDialogueContainer}>
              <AutoHyperlink text={viewer.infoMessage} />
            </div>
          </InfoDialog>

          {this.state.dialogVisible && (
            <Dialog onClose={this.onDialogCancelClick}>
                <DialogHeader title="Wistor App Settings" />
                <DialogContent>
                  <FormGroup>
                    <Label>Layout</Label>
                    <Select
                      className="dark-form-control form-control white-font-override"
                      onChange={(e) => this.selectLayout(e.target.value as any)} // `as any` is fine, as long as options below match
                      value={this.state.viewer.layoutName || ''}
                    >
                      <MenuItem value={LAYOUT_NAME.CUSTOM}>Custom</MenuItem>
                      <MenuItem value={LAYOUT_NAME.ORIGINAL}>Original</MenuItem>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>Title</Label>
                    <Input
                      className="dark-form-control"
                      value={viewer.title || ''}
                      type="text"
                      onChange={(e) => this.updateViewer({ title: e.target.value })}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Subtitle</Label>
                    <Input
                      className="dark-form-control"
                      value={viewer.subtitle || ''}
                      type="text"
                      onChange={(e) => this.updateViewer({ subtitle: e.target.value })}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Info message</Label>
                    <Input
                      className="dark-form-control"
                      value={viewer.infoMessage || ''}
                      type="text"
                      onChange={(e) =>
                        this.setState({
                          viewer: { ...viewer, infoMessage: e.target.value },
                        })
                      }
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      <Input
                        checked={viewer.popupInfoAtStart ?? false}
                        className={styles.settingsCheckbox}
                        type="checkbox"
                        onChange={(e) =>
                          this.setState({
                            viewer: {
                              ...viewer,
                              popupInfoAtStart: e.target.checked,
                            },
                          })
                        }
                      />
                      show popup info at start
                    </Label>
                  </FormGroup>

                  <FormGroup>
                    <Label>pre app javascript</Label>
                    <Input
                      className="dark-form-control"
                      value={viewer.prejs || ''}
                    // @ts-expect-error: DEBT: not sure if this actually does something
                      type="javascript"
                      onChange={(e) => {
                        this.setState({
                          viewer: { ...viewer, prejs: e.target.value },
                        })
                      }}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>bb repo and app parameters</Label>
                    <Input
                      className="dark-form-control"
                      value={viewer.bbsparqlendpoint || ''}
                      type="text"
                      onChange={(e) => {
                        this.setState({
                          viewer: { ...viewer, bbsparqlendpoint: e.target.value },
                        })
                      }}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Sparql endpoint</Label>
                    <Input
                      className="dark-form-control"
                      value={viewer.repoURL || ''}
                      type="text"
                      onChange={(e) =>
                        this.setState({
                          viewer: { ...viewer, repoURL: e.target.value },
                        })
                      }
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>local sparql endpoint (http://localhost:7200/repositories/test)</Label>
                    <Input
                      className="dark-form-control"
                      value={viewer.localRepoURL}
                      type="text"
                      onChange={(e) =>
                        this.setState({
                          viewer: { ...viewer, localRepoURL: e.target.value },
                        })
                      }
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>sparql endpoint error message</Label>
                    <Input
                      className="dark-form-control"
                      value={viewer.sparqlErrorMessage || ''}
                      type="text"
                      onChange={(e) =>
                        this.setState({
                          viewer: {
                            ...viewer,
                            sparqlErrorMessage: e.target.value,
                          },
                        })
                      }
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Logo URL</Label>
                    <Input
                      className="dark-form-control"
                      value={viewer.logoURL || ''}
                      type="text"
                      onChange={(e) =>
                        this.setState({
                          viewer: { ...viewer, logoURL: e.target.value },
                        })
                      }
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label style={{ display: 'flex', gap: '1rem' }}>
                      <div style={{ flexGrow: 1 }}>
                        Global CSS in JSON formaat, bijvoorbeeld: {'{"fontFamily": "Exo"}'}
                      </div>
                      <FontListButton />
                    </Label>
                    <Input
                      className="dark-form-control"
                      value={viewer.css || ''}
                      type="text"
                      onChange={(e) =>
                        this.setState({
                          viewer: { ...viewer, css: e.target.value },
                        })
                      }
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Pagina breedte</Label>
                    <Input
                      className="dark-form-control"
                      value={viewer.pageWidth || ''}
                      type="text"
                      onChange={(e) =>
                        this.setState({
                          viewer: { ...viewer, pageWidth: e.target.value },
                        })
                      }
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>default parameter values </Label>
                    <JsonTextEditor value={viewer.parameterValues || ''}
                                    onValueChange={value => {
                                      this.setState({
                                        viewer: { ...viewer, parameterValues: value },
                                      })
                                    }} />
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      <Input
                        checked={viewer.update60Enabled}
                        className={styles.settingsCheckbox}
                        type="checkbox"
                        onChange={(e) =>
                          this.setState({
                            viewer: {
                              ...viewer,
                              update60Enabled: e.target.checked,
                            },
                          })
                        }
                      />
                      enable update60 parameter refresh at 60s interval
                    </Label>
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      <Input
                        checked={viewer.parseUrlProperties}
                        className={styles.settingsCheckbox}
                        type="checkbox"
                        onChange={(e) =>
                          this.setState({
                            viewer: {
                              ...viewer,
                              parseUrlProperties: e.target.checked,
                            },
                          })
                        }
                      />
                      parse url parameters as property values
                    </Label>
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      <Input
                        checked={viewer.notShowTop}
                        className={styles.settingsCheckbox}
                        type="checkbox"
                        onChange={(e) =>
                          this.setState({
                            viewer: { ...viewer, notShowTop: e.target.checked },
                          })
                        }
                      />{' '}
                      do not show top
                    </Label>
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      <Input
                        checked={viewer.logoutbutton}
                        className={styles.settingsCheckbox}
                        type="checkbox"
                        onChange={(e) =>
                          this.setState({
                            viewer: {
                              ...viewer,
                              logoutbutton: e.target.checked,
                            },
                          })
                        }
                      />
                      show logout button in the header
                    </Label>
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      <Input
                        checked={viewer.notShowBottom}
                        className={styles.settingsCheckbox}
                        type="checkbox"
                        onChange={(e) =>
                          this.setState({
                            viewer: {
                              ...viewer,
                              notShowBottom: e.target.checked,
                            },
                          })
                        }
                      />
                      do not show bottom
                    </Label>
                  </FormGroup>

                  <FormGroup>
                    <LabelWithHelp
                      label={<>App-variables to encode in the URL as an <b>update</b> in the browser's history</>}
                      helpComponent={InfoUpdateURLAppVars}
                    />
                    <Input
                      className="dark-form-control"
                      value={(viewer.updateURLVariables || []).join(", ")}
                      type="text"
                      onChange={(e) =>
                        this.setState({
                          viewer: { ...viewer, updateURLVariables: e.target.value.split(",").map(str => str.trim()) },
                        })
                      }
                    />
                  </FormGroup>

                  <FormGroup>
                    <LabelWithHelp
                      label={<>App-variables to encode in the URL as a <b>push</b> in the browser's history</>}
                      helpComponent={InfoPushURLAppVars}
                    />
                    <Input
                      className="dark-form-control"
                      value={(viewer.pushURLVariables || []).join(", ")}
                      type="text"
                      onChange={(e) =>
                        this.setState({
                          viewer: { ...viewer, pushURLVariables: e.target.value.split(",").map(str => str.trim()) },
                        })
                      }
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Config in JSON formaat</Label>
                    <JsonEditor
                      exportFileName={`${this.state.viewer?.project?.path}-${this.state.viewer?.path}-config.${formatDate(new Date(), 'yyyy-MM-dd.HHmm')}.json`}
                      value={viewer}
                      onValueChange={config => {
                        if (config == null) return
                        this.setState(prevState => ({
                          viewer: {
                            ...config,
                            id: prevState.viewer.id,
                            user: prevState.viewer.user,
                            path: prevState.viewer.path,
                            project: prevState.viewer.project,
                            createdAt: prevState.viewer.createdAt,
                          }
                        } as any)) // DEBT: this could introduce a wrongly typed viewer
                      }} />
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      <Input
                        checked={viewer.public}
                        className={styles.settingsCheckbox}
                        type="checkbox"
                        onChange={(e) =>
                          this.setState({
                            viewer: { ...viewer, public: e.target.checked },
                          })
                        }
                      />
                      publiek
                    </Label>
                  </FormGroup>
                </DialogContent>
                <DialogFooter>
                  <Button
                    color="secondary"
                    style={{ minWidth: '120px' }}
                    onClick={this.onDialogCancelClick}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    style={{ minWidth: '120px' }}
                    onClick={this.onDialogOkClick}
                  >
                    OK
                  </Button>
                </DialogFooter>
            </Dialog>
          )}
        </div>
        <ToastContainer
          position="bottom-right"
          autoClose={3000}
          hideProgressBar
          newestOnTop={true}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </div>
      </RuleExecutionLogsStoreProvider>
      </EventLogStoreProvider>
    )
  }
}

export default function Viewer(props: RouteComponentProps) {
  const auth = useWistorAuth()
  const dispatch = useDispatch()
  const project = useSelector(s => s.project)
  const pubsub = useSelector(s => s.pubsub)
  const flags = useFeatureFlags()

  return (
    <ViewerComponent
      {...props}
      auth={auth}
      dispatch={dispatch}
      project={project}
      pubsub={pubsub}
      flags={flags}
    />
  )
}

export function shouldConnectToWistorBackend(
  config: Pick<Viewer, 'localRepoURL' | 'deleted' | 'bbsparqlendpoint'>
) {
  const isConnectedToLocalRepo = config.localRepoURL?.includes(':7200') && window.location.host === 'localhost:3000'
  if (isConnectedToLocalRepo) return false
  if (config.deleted) return false
  if (window.location.search.includes("debug=skiplogin")) return false
  return Boolean(config.bbsparqlendpoint?.includes?.('repo='))
}

function getViewProps(props: Props) {
  if (props.flags.localMode) {
    return {
      mode: (props.match.params as any).mode as 'edit' | 'preview',
      projectPath: '',
      viewerPath: '',
    }
  }
  if (window.CONNECTED_APPS_STATIC)
    return {
      mode: 'preview' as const,
      projectPath: window.PROJECT as string,
      viewerPath: window.VIEWER as string,
    }

  return {
    mode: (props.match.params as any).mode as 'edit' | 'preview',
    projectPath: (props.match.params as any).projectPath as string,
    viewerPath: (props.match.params as any).viewerPath as string,
  }
}

function InfoUpdateURLAppVars() {
  return <>
    Changes in app-variables that are configured here will overwrite the current browser location.
    <i>The browser will be unable to go back in history to the previous set of app-variables.</i>
  </>
}

function InfoPushURLAppVars() {
  return <>
    Changes in app-variables that are configured here will push the current browser location.
    <i>The browser will be able to go back in history to the previous set of app-variables.</i>
  </>
}
